import { logEvent, setUserId, getAnalytics, setUserProperties } from "firebase/analytics";
import { AppPlatform, LimitFeature, AnalyticsEventType } from "app/types";

// GA に送るユーザープロパティ（名前・値は docs/analytics-events.md の表のとおり）
export type AnalyticsUserProperties = {
  plan: string;
  account_type: string;
};

/**
 * ネイティブの Firebase に渡すか。Amazon 版のアプリはネイティブの Firebase が使えないので、
 * WebView の中でも Web の Firebase で送る
 */
export const usesNativeAnalytics = () =>
  !!window.ReactNativeWebView && window.kidsAppPlatform !== AppPlatform.Amazon;

const postNative = (command: string, data: object) => {
  window.ReactNativeWebView.postMessage(JSON.stringify({ command, data }));
};

const withPage = (params?: object) => ({
  ...params,
  page_path: location.pathname,
  page_title: location.pathname,
});

/**
 * イベントを GA に送る。アプリ（WebView）ではネイティブの Firebase に渡す（Amazon 版を除く）。
 * React の外（api.ts など）からも呼べるように、hook にしていない。
 */
export const recordEvent = (type: AnalyticsEventType | string, params?: object) => {
  try {
    if (usesNativeAnalytics()) {
      postNative("logEvent", { event: type, params: withPage(params) });
    } else {
      logEvent(getAnalytics(), type, withPage(params));
    }
  } catch (error) {
    console.warn("recordEvent error:", error);
  }
};

/** GA のユーザー ID。遊んでいる子ども（ゲストを含む）の uid。null で外す */
export const setAnalyticsUserId = (uid: string | null) => {
  try {
    if (usesNativeAnalytics()) {
      postNative("setUserId", { uid });
    } else {
      setUserId(getAnalytics(), uid);
    }
  } catch (error) {
    console.warn("setAnalyticsUserId error:", error);
  }
};

// LIMIT_HIT に付けるため、最後に送ったプランを覚えておく
let currentPlan: string | null = null;

export const setAnalyticsUserProperties = (properties: AnalyticsUserProperties) => {
  currentPlan = properties.plan;
  try {
    if (usesNativeAnalytics()) {
      postNative("setUserProperties", { properties });
    } else {
      setUserProperties(getAnalytics(), properties);
    }
  } catch (error) {
    console.warn("setAnalyticsUserProperties error:", error);
  }
};

/** 上限に当たった。React の外（api.ts）からも呼ぶ */
export const recordLimitHit = (feature: LimitFeature) => {
  recordEvent(AnalyticsEventType.LIMIT_HIT, { feature, plan: currentPlan ?? "unknown" });
};

/** SSE の onError で、上限の code が来たら LIMIT_HIT を送るようにする */
export const withLimitHit = <T extends { onError: (code: string, message: string) => void }>(
  callbacks: T,
  feature: LimitFeature,
  limitCodes: string[],
): T => ({
  ...callbacks,
  onError: (code: string, message: string) => {
    if (limitCodes.includes(code)) {
      recordLimitHit(feature);
    }
    callbacks.onError(code, message);
  },
});
